import { useCallback, useMemo, useState } from 'react'
import { AdminTable, type Column } from './AdminTable'
import { Pagination } from './Pagination'
import { LoadingBar } from './LoadingBar'
import { EnrichmentRunDetail } from './EnrichmentRunDetail'
import { useEnrichmentRunFeed } from '@/hooks/useEnrichmentRunFeed'
import { cancelEnrichmentRun } from '@/api/readings'
import { isEnrichmentRunStopping } from '@/types/reading'
import type { AdminEnrichmentRun } from '@/types/admin'
import { focusRing, quietFill } from '@/components/ui/buttonStyles'

interface EnrichmentRunsTabProps {
  active: boolean
}

/**
 * Every enrichment run, across every reading.
 *
 * The reading page can only ever show one reading's latest run, which leaves an admin
 * unable to answer "is anything running?" or "why has my text not started?" — and
 * because only one text enriches at a time, a run waiting behind another looks exactly
 * like a run that never started. This tab is where that becomes visible.
 */
export function EnrichmentRunsTab({ active }: EnrichmentRunsTabProps) {
  const feed = useEnrichmentRunFeed(active)
  const { refresh } = feed
  const [stoppingIds, setStoppingIds] = useState<number[]>([])
  // Tab-local, not a route: the admin panel's tabs are client state, and one drill-in
  // does not justify the panel's first sub-route (research R10).
  const [openRunId, setOpenRunId] = useState<number | null>(null)

  const columns = useMemo(() => historyColumns(setOpenRunId), [])

  const stop = useCallback(
    async (run: AdminEnrichmentRun) => {
      setStoppingIds((ids) => [...ids, run.id])
      try {
        // The per-reading endpoint already handles losing the race to a run that
        // finished on its own, so there is no second cancel path to keep correct.
        await cancelEnrichmentRun(run.reading.id)
      } catch {
        // Losing that race is not an error (FR-009): the refresh below shows the truth.
      } finally {
        await refresh()
        setStoppingIds((ids) => ids.filter((id) => id !== run.id))
      }
    },
    [refresh],
  )

  if (!active) return null

  if (openRunId !== null) {
    return <EnrichmentRunDetail runId={openRunId} onBack={() => setOpenRunId(null)} />
  }

  return (
    <div className="flex flex-col gap-10">
      <section className="flex flex-col gap-4">
        <h2 className="font-mono text-xs uppercase tracking-widest text-muted">In progress</h2>

        {feed.loading && <LoadingBar />}

        {feed.error && (
          <p role="alert" className="font-light text-sm text-muted">
            {feed.error}
          </p>
        )}

        {!feed.loading && !feed.error && feed.active.length === 0 && (
          <p className="font-light text-sm text-muted">
            Nothing is being enriched right now.
          </p>
        )}

        {feed.active.map((run) => (
          <ActiveRunCard
            key={run.id}
            run={run}
            position={queuePosition(feed.active, run)}
            busy={stoppingIds.includes(run.id)}
            onStop={() => void stop(run)}
            onOpen={() => setOpenRunId(run.id)}
          />
        ))}
      </section>

      <section className="flex flex-col gap-4">
        <h2 className="font-mono text-xs uppercase tracking-widest text-muted">Finished</h2>
        <AdminTable columns={columns} data={feed.history} keyFn={(run) => run.id} />
        <Pagination meta={feed.meta} onPageChange={feed.setPage} />
      </section>
    </div>
  )
}

function historyColumns(open: (id: number) => void): Column<AdminEnrichmentRun>[] {
  return [
    {
      key: 'reading',
      label: 'Reading',
      render: (run) => (
        <button
          type="button"
          onClick={() => open(run.id)}
          aria-label={`Open run for ${run.reading.title}`}
          className="text-left min-h-11 text-body transition-colors duration-200 hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 rounded"
        >
          {run.reading.title}
        </button>
      ),
    },
    { key: 'language', label: 'Language', render: (run) => run.reading.language_name },
    { key: 'status', label: 'Status', render: (run) => run.status },
    { key: 'enriched', label: 'Enriched', render: (run) => run.enriched_count },
    { key: 'skipped', label: 'Already present', render: (run) => run.skipped_count },
    { key: 'unresolved', label: 'Not found', render: (run) => run.unresolved_count },
    { key: 'names', label: 'Names', render: (run) => run.proper_noun_count },
    { key: 'finished', label: 'Finished', render: (run) => finishedAt(run) },
  ]
}

function finishedAt(run: AdminEnrichmentRun): string {
  if (!run.finished_at) return '—'

  return new Date(run.finished_at).toLocaleString()
}

interface ActiveRunCardProps {
  run: AdminEnrichmentRun
  position: number
  busy: boolean
  onStop: () => void
  onOpen: () => void
}

function ActiveRunCard({ run, position, busy, onStop, onOpen }: ActiveRunCardProps) {
  // Once asked, the run keeps working until its next word. Offering the button again
  // would invite a press that does nothing.
  const stopping = isEnrichmentRunStopping(run) || busy

  return (
    <article className="rounded-md border border-border-divider bg-surface-bg px-4 py-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          {/* FR-002: a run does not have to finish before its evidence is worth reading. */}
          <h3>
            <button
              type="button"
              onClick={onOpen}
              aria-label={`Open run for ${run.reading.title}`}
              className="text-left min-h-11 font-serif text-base text-heading transition-colors duration-200 hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 rounded"
            >
              {run.reading.title}
            </button>
          </h3>
          <p className="font-mono text-xs uppercase tracking-widest text-muted mt-1">
            {run.reading.language_name} · {run.started_by}
          </p>
        </div>

        {!stopping && (
          <button
            type="button"
            onClick={onStop}
            aria-label={`Stop enriching ${run.reading.title}`}
            className={`shrink-0 inline-flex items-center min-h-11 px-3 rounded text-xs font-normal transition-colors duration-200 ${quietFill} ${focusRing}`}
          >
            Stop
          </button>
        )}
      </div>

      {/* Announced, not merely drawn, so progress is not sight-only. */}
      <p aria-live="polite" className="font-mono text-xs tracking-wide text-muted mt-3">
        {statusLine(run, position, stopping)}
      </p>
    </article>
  )
}

/**
 * 1-based place among the runs still waiting. Counted from the queued runs only: when
 * nothing is running (worker down, or a run just finished) the whole list is the queue,
 * and counting from the top would call two runs "next".
 */
function queuePosition(active: AdminEnrichmentRun[], run: AdminEnrichmentRun): number {
  return active.filter((candidate) => candidate.status === 'queued').indexOf(run) + 1
}

/**
 * A waiting run must say *why* it is not moving. "Queued" alone reads as broken; its
 * place in the line reads as working-as-intended (FR-004).
 */
function statusLine(run: AdminEnrichmentRun, position: number, stopping: boolean): string {
  if (stopping) return `Stopping… ${run.processed_words} of ${run.total_words} words`
  if (run.status === 'queued') return `Waiting · ${queuePlace(position)} in line`

  return `${run.processed_words} of ${run.total_words} words`
}

function queuePlace(position: number): string {
  if (position === 1) return 'next'

  return `${position}${ordinalSuffix(position)}`
}

function ordinalSuffix(n: number): string {
  if (n % 100 >= 11 && n % 100 <= 13) return 'th'

  return { 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] ?? 'th'
}
